import {
  addMonths,
  formatDateISO,
  parseDateISO,
  startOfMonth,
  startOfWeek,
  today,
} from '@/app/shared/utils';
import { DateRangePreset, DateRangeValue } from './date-range-selector.model';

export const MAX_CUSTOM_RANGE_MONTHS = 3;

export const DATE_RANGE_PRESET_OPTIONS: DateRangePreset[] = [
  'THIS_WEEK',
  'THIS_MONTH',
  'LAST_THREE_MONTHS',
  'CUSTOM',
];

export const DATE_RANGE_PRESET_LABEL: Record<DateRangePreset, string> = {
  THIS_WEEK: 'Esta semana',
  THIS_MONTH: 'Este mes',
  LAST_THREE_MONTHS: 'Últimos 3 meses',
  CUSTOM: 'Personalizado (hasta 3 meses)',
};

export const DEFAULT_DATE_RANGE_VALUE: DateRangeValue = {
  preset: 'THIS_WEEK',
  startDate: null,
  endDate: null,
};

export function computePresetRange(
  preset: DateRangePreset,
  referenceDate: Date = today(),
): { startDate: string; endDate: string } | null {
  switch (preset) {
    case 'THIS_WEEK':
      return {
        startDate: formatDateISO(startOfWeek(referenceDate)),
        endDate: formatDateISO(referenceDate),
      };
    case 'THIS_MONTH':
      return {
        startDate: formatDateISO(startOfMonth(referenceDate)),
        endDate: formatDateISO(referenceDate),
      };
    case 'LAST_THREE_MONTHS':
      return {
        startDate: formatDateISO(addMonths(referenceDate, -MAX_CUSTOM_RANGE_MONTHS)),
        endDate: formatDateISO(referenceDate),
      };
    case 'CUSTOM':
      return null;
  }
}

export function clampEndDate(
  startDate: string | null,
  endDate: string | null,
  maxMonths = MAX_CUSTOM_RANGE_MONTHS,
): string | null {
  if (!startDate || !endDate) return endDate;

  const start = parseDateISO(startDate);
  const end = parseDateISO(endDate);
  if (end < start) return startDate;

  const maxEnd = addMonths(start, maxMonths);
  return end > maxEnd ? formatDateISO(maxEnd) : endDate;
}

export function clampStartDate(
  startDate: string | null,
  endDate: string | null,
  maxMonths = MAX_CUSTOM_RANGE_MONTHS,
): string | null {
  if (!startDate || !endDate) return startDate;

  const start = parseDateISO(startDate);
  const end = parseDateISO(endDate);
  if (start > end) return endDate;

  const minStart = addMonths(end, -maxMonths);
  return start < minStart ? formatDateISO(minStart) : startDate;
}

export function maxEndDateAttr(
  startDate: string | null,
  maxMonths = MAX_CUSTOM_RANGE_MONTHS,
): string | null {
  return startDate ? formatDateISO(addMonths(parseDateISO(startDate), maxMonths)) : null;
}

export function minStartDateAttr(
  endDate: string | null,
  maxMonths = MAX_CUSTOM_RANGE_MONTHS,
): string | null {
  return endDate ? formatDateISO(addMonths(parseDateISO(endDate), -maxMonths)) : null;
}

export function clampNotFutureDate(
  date: string | null,
  referenceDate: Date = today(),
): string | null {
  if (!date) return date;
  return parseDateISO(date) > referenceDate ? formatDateISO(referenceDate) : date;
}

export function earlierDate(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return a < b ? a : b;
}
